import { Text, vars } from '@etg/wings';
import { css, cx } from '@eti/styles';
import { useEffect, useRef } from 'react';
import type { SearchLocationsQuery } from '@eti/schema-types';
import { useTranslation } from '@eti/providers';

const displayBlockStyles = css`
  display: block;
`;

const listboxStyles = css`
  background-color: ${vars.colors.surface.primary};
  border: 0.0625rem solid ${vars.colors.inputs.border};
  border-end-end-radius: 0.1875rem;
  border-end-start-radius: 0.1875rem;
  border-top-width: 0;
  display: none;
  left: 0;
  max-height: 25rem;
  min-height: 3rem;
  overflow: auto;
  position: absolute;
  right: 0;
  z-index: 4;
`;

const listboxWrapperStyles = css`
  position: relative;
`;

const listItemContentStyles = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const listItemStyles = css`
  cursor: default;
  display: flex;
  padding: 1rem;
  place-items: center;
`;

const titleStyles = css`
  color: ${vars.colors.text.primary};
`;

const locationStyles = css`
  color: ${vars.colors.text.secondary};
`;

const optionNavigatedStyles = css`
  background-color: ${vars.colors.inputs.selected};
`;

const textSelectedStyles = css`
  color: ${vars.colors.inputs.main};
`;

const textCenteredStyles = css`
  justify-content: center;
`;

const optionSelectedStyles = css`
  background-color: ${vars.colors.inputs.hover};
`;

const optionIndentedStyles = css`
  padding-inline-start: 2rem;
`;

interface LocationListBoxProps {
  highlightedIndex: number;
  id: string;
  isLoading: boolean;
  isOpen: boolean;
  isSearchTermLongEnough: boolean;
  navigatedIndex: number;
  onMouseLeave: () => void;
  onMouseMove: (index: number) => void;
  onOptionSelect: (option: SearchLocationsQuery['searchLocations'][number]) => void;
  options: SearchLocationsQuery['searchLocations'];
  selectedOptionId?: number;
}

const LocationListBox = ({
  highlightedIndex,
  id,
  isLoading,
  isOpen,
  isSearchTermLongEnough,
  navigatedIndex,
  onMouseLeave,
  onMouseMove,
  onOptionSelect,
  options,
  selectedOptionId,
}: LocationListBoxProps) => {
  const { t } = useTranslation();
  const listboxRef = useRef<HTMLUListElement>(null);
  const handleOnMouseMove = (index: number) => () => {
    onMouseMove(index);
  };

  const handleOnOptionSelect = (option: SearchLocationsQuery['searchLocations'][number]) => () => {
    onOptionSelect(option);
  };

  useEffect(() => {
    const listbox = listboxRef.current;
    if (!listbox) {
      return;
    }

    const selectedElement = listbox.querySelector(
      'li[aria-selected="true"]',
    ) as HTMLLIElement | null;

    selectedElement?.scrollIntoView({ block: 'nearest' });
  }, [navigatedIndex]);

  return (
    <div className={listboxWrapperStyles}>
      <ul
        className={cx(listboxStyles, isOpen ? displayBlockStyles : undefined)}
        id={`${id}-listbox`}
        ref={listboxRef}
        role="listbox"
        tabIndex={-1}
      >
        {isLoading && (
          <li className={cx(listItemStyles, textCenteredStyles)} role="presentation" tabIndex={-1}>
            <span className={listItemContentStyles}>{t('Searchform.Loading.Option.Text')}</span>
          </li>
        )}
        {!isLoading && !isSearchTermLongEnough && options.length === 0 && (
          <li className={listItemStyles} role="presentation" tabIndex={-1}>
            <span className={listItemContentStyles}>
              {t('Air.Search.CitiesSelection.SearchHint')}
            </span>
          </li>
        )}
        {!isLoading && isSearchTermLongEnough && options.length === 0 && (
          <li className={listItemStyles} role="presentation" tabIndex={-1}>
            <span className={listItemContentStyles}>
              {t('Searchform.NoResultsFound.Option.Text')}
            </span>
          </li>
        )}
        {!isLoading &&
          options.map((option, index) => {
            if (!option) {
              return null;
            }

            const title = `${option.name}${
              option.multipleAirports ? ` (${t('Air.Search.AllAirports')})` : ''
            }`;
            const location = `, ${option.country}${option.state ? `, ${option.state}` : ''}`;

            return (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                aria-selected={highlightedIndex === index}
                className={cx(
                  listItemStyles,
                  option.id === selectedOptionId ? optionSelectedStyles : undefined,
                  highlightedIndex === index ? optionNavigatedStyles : undefined,
                  option.type !== 'CITY' ? optionIndentedStyles : undefined,
                )}
                id={`${id}-option-${index}`}
                key={option.id}
                onClick={handleOnOptionSelect(option)}
                onMouseLeave={onMouseLeave}
                onMouseMove={handleOnMouseMove(index)}
                role="option"
                tabIndex={-1}
              >
                <div className={listItemContentStyles}>
                  <Text
                    as="span"
                    className={cx(
                      titleStyles,
                      option.id === selectedOptionId ? textSelectedStyles : undefined,
                    )}
                  >
                    {title}
                  </Text>
                  <Text
                    as="span"
                    className={cx(
                      locationStyles,
                      option.id === selectedOptionId ? textSelectedStyles : undefined,
                    )}
                  >
                    {location}
                  </Text>
                </div>
              </li>
            );
          })}
      </ul>
      <span
        aria-label={
          isOpen && isSearchTermLongEnough
            ? t('ScreenReader.SearchForm.NumberOfResultsFound', options.length)
            : undefined
        }
        aria-live="polite"
        role="alert"
      />
    </div>
  );
};

export default LocationListBox;
